/**
 * Cache Storage housekeeping for the offline read cache.
 *
 * Everything here is best-effort and never throws: the Cache API is absent in
 * non-secure contexts and inside the Capacitor native shell (where no service
 * worker is registered at all), and deleting a cache that was never created
 * simply resolves `false`.
 */
import {
  EXPECTED_RUNTIME_CACHES,
  RUNTIME_CACHE_FAMILIES,
  USER_RUNTIME_CACHES,
} from '@/services/cache-names';

/**
 * Drop every cache holding data for the signed-in user.
 *
 * Called at BOTH auth boundaries — on sign-out (so nothing survives for the
 * next person on the device) and on sign-in (so a failed sign-out purge, or a
 * different tenant, can never leak into the new session).
 *
 * Fire-and-forget by design: `signOutLocal()` is synchronous and must stay
 * responsive, and the caches also expire on their own after 7 days.
 */
export function purgeUserCaches(): void {
  try {
    if (typeof caches === 'undefined') return;
    void Promise.all(USER_RUNTIME_CACHES.map((name) => caches.delete(name))).catch(() => {
      /* no-op (storage error) — entries expire on their own */
    });
  } catch {
    /* no-op — touching `caches` itself throws in a non-secure context */
  }
}

/**
 * Delete runtime caches left behind by a superseded build — i.e. a cache in one
 * of our {@link RUNTIME_CACHE_FAMILIES} that is not in
 * {@link EXPECTED_RUNTIME_CACHES}.
 *
 * With `generateSW` there is no custom `activate` handler to do this, so it
 * runs once at app boot instead. Bumping a cache name in cache-names.ts is
 * therefore all that is needed to retire the old one.
 *
 * Matching is restricted to those families on purpose: Workbox names the
 * precache after the `cacheId` option (`syntry-precache-v2-<scope>`), so a
 * looser `syntry-` match would delete the app shell on every boot.
 */
export async function cleanupStaleRuntimeCaches(): Promise<void> {
  try {
    if (typeof caches === 'undefined') return;
    const names = await caches.keys();
    const stale = names.filter(
      (name) =>
        RUNTIME_CACHE_FAMILIES.some((family) => name.startsWith(family)) &&
        !EXPECTED_RUNTIME_CACHES.includes(name),
    );
    await Promise.all(stale.map((name) => caches.delete(name)));
  } catch {
    /* no-op — housekeeping must never break boot */
  }
}
